import { Show } from "solid-js";
import { SolidStreamingMarkdown } from "@storytell/markdown";
import type { StreamingBlock } from "@storytell/streaming";
import { twMerge } from "tailwind-merge";
import { LogoNoText } from "~/components/brand";
import { useWire } from "~/wire";
import { createMarkdownStreamerFromDeltas } from "./internal/markdownStreamerFromDeltas";
import { Features } from "@storytell/cp-client";

interface UserFacingMessageProps {
  block: StreamingBlock;
}

export const UserFacingMessage = (props: UserFacingMessageProps) => {
  const wire = useWire();
  const operation = () => wire.stream.operation.get(props.block.operationId);

  const hideContentOnComplete = () => {
    const op = operation();
    if (!op) return false;
    const activity = op.activity;
    if (activity === "user_kb_search_v1" && op.isComplete) {
      return true;
    }
    return false;
  };

  const hasContent = () => !!props.block.deltas.length && !hideContentOnComplete();

  // Create streaming markdown instance
  const markdownStreamer = createMarkdownStreamerFromDeltas(() => props.block.deltas.map((delta) => delta.text));

  // TODO: @andi streaming Bring back the direct response styling
  return (
    <Show when={props.block} fallback={<div class="text-sm text-gray-700 dark:text-gray-300">Loading...</div>}>
      <Show when={hasContent()}>
        <div
          data-renderer="user-facing-message"
          class={twMerge(
            "text-sm text-primary-text",
            operation()?.activity === "direct_response_v1" &&
              "text-base bg-panel pl-5 pr-5 -ml-2 pt-3 pb-3 rounded-xl border border-primary-border",
          )}
        >
          <Show when={operation()?.activity === "direct_response_v1"}>
            <div class="flex items-center gap-2 mb-3 -ml-1">
              <LogoNoText class="w-5 h-5" />
            </div>
          </Show>
          <div class="prose prose-sm dark:prose-invert max-w-none">
            <SolidStreamingMarkdown
              doc={markdownStreamer.doc}
              options={{
                classes: {
                  document: "wrap-break-word whitespace-break-spaces",
                  heading: (node) => {
                    switch (node.level) {
                      case 1:
                        return "font-medium text-lg mb-2 mt-3";
                      case 2:
                        return "font-medium text-base mb-2 mt-3";
                      case 3:
                        return "font-medium text-sm mb-2 mt-3";
                      default:
                        return "font-medium text-sm mb-2 mt-3";
                    }
                  },
                  paragraph: "mb-2",
                  list: (node) => {
                    switch (node.listData?.type) {
                      case "ordered":
                        return "list-decimal pl-4";
                      case "bullet":
                        return "list-disc pl-4";
                      default:
                        return "";
                    }
                  },
                  item: "mb-1",
                  code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-xs",
                  blockQuote: "border-l-2 border-blue-100 dark:border-blue-700 pl-2 py-1 my-1",
                  link: "dark:text-dark-primary underline",
                },
              }}
            />
          </div>
          <Show when={!props.block.isComplete}>
            <div class="inline-block w-2 h-4 bg-blue-500 animate-pulse ml-1" />
          </Show>
        </div>
      </Show>
    </Show>
  );
};
